"use client";

import { motion, useAnimation, useReducedMotion, Variants } from "framer-motion";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

export interface SearchIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface SearchIconProps {
  size?: number;
  duration?: number;
  isAnimated?: boolean;
  isHovered?: boolean;
  className?: string;
}

const SearchIcon = forwardRef<SearchIconHandle, SearchIconProps>(
  (
    {
      className,
      size = 24,
      duration = 1,
      isAnimated = true,
      isHovered = false,
    },
    ref,
  ) => {
    const controls = useAnimation();
    const reduced = useReducedMotion();
    const isControlled = useRef(false);
    const prevHovered = useRef(isHovered);

    useImperativeHandle(ref, () => {
      isControlled.current = true;
      return {
        startAnimation: () =>
          reduced ? controls.start("normal") : controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    // React to isHovered prop changes
    if (isHovered !== prevHovered.current) {
      prevHovered.current = isHovered;
      if (isHovered && isAnimated && !reduced) {
        controls.start("animate");
      } else if (!isHovered) {
        controls.start("normal");
      }
    }

    const handleEnter = useCallback(() => {
      if (!isAnimated || reduced) return;
      if (!isControlled.current) controls.start("animate");
    }, [controls, reduced, isAnimated]);

    const handleLeave = useCallback(() => {
      if (!isControlled.current) controls.start("normal");
    }, [controls]);

    const lensVariants: Variants = {
      normal: { x: 0, y: 0, rotate: 0, opacity: 1 },
      animate: {
        x: [0, 2, -2, 1, 0],
        y: [0, -1, 2, -1, 0],
        rotate: [0, 6, -6, 4, 0],
        transition: {
          duration: 1.2 * duration,
          ease: "easeInOut" as const,
        },
      },
    };

    return (
      <motion.svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        style={{ flexShrink: 0 }}
        animate={controls}
        initial="normal"
      >
        <motion.g variants={lensVariants}>
          <motion.circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.34-4.34" />
        </motion.g>
      </motion.svg>
    );
  },
);

SearchIcon.displayName = "SearchIcon";
export { SearchIcon };
